import { createBrowserRouter, type RouteObject } from 'react-router';

import { AppShell } from '@shared/app-shell';
import { authRoutes as authFeatureRoutes } from '@features/auth/auth.routes';
import { contentRoutes } from '@features/content/content.routes';
import { editorialRoutes } from '@features/editorial/editorial.routes';
import { casesRoutes } from '@features/cases/cases.routes';
import { indexRoute } from '@features/index/index.route';
import { subscriptionRoutes } from '@features/subscription/subscription.routes';
import { inboxRoute } from '@features/inbox/inbox.route';
import { moderationRoutes } from '@features/moderation/moderation.routes';
import { pagesRoutes } from '@features/pages/pages.routes';
import { peopleRoutes } from '@features/people/people.routes';
import { settingsRoutes } from '@features/settings/settings.routes';
import { shelvesRoute } from '@features/shelves/shelves.route';
import { systemRoutes } from '@features/system/system.routes';
import { taxonomyRoutes } from '@features/taxonomy/taxonomy.routes';
import { usersRoutes } from '@features/users/users.routes';

import { consoleLoader } from './guards';
import { PendingRoute } from './PendingRoute';
import { RootErrorFallback } from './RootErrorFallback';

/**
 * The composition root. **This file is the only place a feature is referenced
 * by name** — every other module imports from `@shared/…` only.
 *
 * Each feature exports its own `<page-slug>.route.tsx` (singular, one
 * `RouteObject`) or `<feature>.routes.tsx` (plural, an aggregate), and one line
 * here pulls it in. Bootstrap ships the lists empty: the routes land with
 * `/implement frontend-page`, one surface at a time.
 *
 * Two shells, and the split is the session: the auth screens carry half the
 * shell and drop the half that assumes one, so they sit outside the console
 * layout rather than inside it with pieces hidden.
 *
 * The console shell is mounted **twice**, and the difference is the guard rather
 * than the chrome. The system pair carries the shell whole — it is the surface
 * where an operator most needs a way out — but neither page may sit behind the
 * guard chain: `/500` exists to say the service did not answer, and the chain's
 * own `GET /api/v1/me` is one of the calls that can fail, while a URL naming no
 * surface is not a thing a sign-in makes reachable. Neither page reads a gated
 * endpoint, so there is nothing for a guard to protect.
 */

/** The three sign-in screens — no session, no console chrome, no guard. */
const authRoutes: RouteObject[] = authFeatureRoutes;

/** Everything behind the guard, rendered inside `<AppShell>`. */
const consoleRoutes: RouteObject[] = [
  indexRoute,
  inboxRoute,
  shelvesRoute,
  ...taxonomyRoutes,
  ...editorialRoutes,
  ...subscriptionRoutes,
  ...usersRoutes,
  ...peopleRoutes,
  ...moderationRoutes,
  ...casesRoutes,
  ...contentRoutes,
  ...pagesRoutes,
  ...settingsRoutes,
];

export const router = createBrowserRouter([
  ...authRoutes,
  {
    id: 'console',
    element: <AppShell />,
    loader: consoleLoader,
    errorElement: <RootErrorFallback />,
    HydrateFallback: PendingRoute,
    children: consoleRoutes,
  },
  {
    id: 'system',
    element: <AppShell />,
    children: systemRoutes,
  },
]);
